import { Link, useRouteError, isRouteErrorResponse } from 'react-router';
import { ArrowLeftIcon, LockIcon, SearchXIcon, ServerCrashIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useT } from '@/lib/i18n';

/**
 * Error pages: 403 / 404 / 500.
 *
 * Deliberately free of internal detail — no stack traces, no request internals.
 * Where a traceId exists it is shown, because that is the one thing that lets
 * support correlate a user's report with logs, audit rows and webhooks
 * (build/10 §7.1).
 */
function ErrorShell({
  code, title, description, icon: Icon, traceId,
}: {
  code: string;
  title: string;
  description: string;
  icon: typeof LockIcon;
  traceId?: string;
}) {
  const t = useT();
  return (
    <div className="bg-background flex min-h-[60vh] flex-col items-center justify-center px-4 py-16 text-center">
      <div className="bg-muted text-muted-foreground mb-5 flex size-14 items-center justify-center rounded-2xl">
        <Icon className="size-6" />
      </div>
      <p className="text-muted-foreground font-mono text-sm">{code}</p>
      <h1 className="mt-2 text-2xl font-semibold tracking-tight">{title}</h1>
      <p className="text-muted-foreground mt-2 max-w-md text-sm">{description}</p>

      {traceId && (
        <p className="text-muted-foreground mt-4 font-mono text-xs">
          {t('error.traceId')} <span className="text-foreground">{traceId}</span>
        </p>
      )}

      <div className="mt-6 flex gap-2">
        <Button asChild size="sm">
          <Link to="/">
            {/* Not mirrored by hand: `dir` on <html> flips this icon's box, and
                the arrow points back either way because the sidebar and the
                content order flip with it. */}
            <ArrowLeftIcon className="size-4 rtl:rotate-180" />
            {t('error.backToDashboard')}
          </Link>
        </Button>
      </div>
    </div>
  );
}

export const Forbidden = () => {
  const t = useT();
  return (
    <ErrorShell
      code="403" icon={LockIcon} title={t('error.403.title')}
      description={t('error.403.description')}
    />
  );
};

export const NotFound = () => {
  const t = useT();
  return (
    <ErrorShell
      code="404" icon={SearchXIcon} title={t('error.404.title')}
      description={t('error.404.description')}
    />
  );
};

export const ServerError = ({ traceId }: { traceId?: string }) => {
  const t = useT();
  return (
    <ErrorShell
      code="500" icon={ServerCrashIcon} title={t('error.500.title')}
      description={t('error.500.description')}
      traceId={traceId}
    />
  );
};

/**
 * Router-level boundary. React Router hands both thrown responses and
 * unexpected exceptions here, so map 403/404 onto their own pages and treat
 * anything else as a 500.
 */
export function RouteErrorBoundary() {
  const error = useRouteError();

  if (isRouteErrorResponse(error)) {
    if (error.status === 403) return <Forbidden />;
    if (error.status === 404) return <NotFound />;
  }
  return <ServerError />;
}
